(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  const SHOP_ALL_URL = `${ROOT}collections/shop-all`;

  class ZBCartPage extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.noteTimer = null;
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.fixShoppingLinks();
      this.refreshVariantPickers();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      window.clearTimeout(this.noteTimer);
    }

    async onClick(event) {
      const quantityButton = event.target.closest('[data-zb-qty-action]');
      if (quantityButton) {
        event.preventDefault();
        const item = quantityButton.closest('[data-zb-cart-item]');
        const input = item?.querySelector('[data-zb-quantity]');
        if (!item || !input || this.busy) return;

        const step = Number(input.step) || 1;
        const min = Number(input.min) || 0;
        const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const direction = quantityButton.dataset.zbQtyAction === 'plus' ? 1 : -1;
        const nextQuantity = Math.min(max, Math.max(min, Number(input.value || 0) + direction * step));
        if (nextQuantity === Number(input.value)) return;

        input.value = nextQuantity;
        await this.changeQuantity(item.dataset.lineKey, nextQuantity);
        return;
      }

      const removeButton = event.target.closest('[data-zb-remove]');
      if (removeButton) {
        event.preventDefault();
        if (this.busy) return;
        await this.changeQuantity(removeButton.dataset.lineKey, 0);
        return;
      }

      const variantButton = event.target.closest('[data-zb-variant-update]');
      if (variantButton) {
        event.preventDefault();
        if (this.busy || variantButton.disabled) return;
        await this.changeVariant(variantButton.closest('[data-zb-variant-picker]'));
      }
    }

    async onChange(event) {
      const quantityInput = event.target.closest('[data-zb-quantity]');
      if (quantityInput) {
        if (this.busy) return;
        const item = quantityInput.closest('[data-zb-cart-item]');
        if (!item) return;
        const quantity = this.normaliseQuantity(quantityInput);
        quantityInput.value = quantity;
        await this.changeQuantity(item.dataset.lineKey, quantity);
        return;
      }

      const optionSelect = event.target.closest('[data-zb-option]');
      if (optionSelect) {
        if (this.busy) return;
        this.previewVariant(optionSelect.closest('[data-zb-variant-picker]'));
      }
    }

    onInput(event) {
      const note = event.target.closest('[data-zb-cart-note]');
      if (!note) return;
      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => this.saveNote(note.value), 500);
    }

    normaliseQuantity(input) {
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const raw = Number(input.value);
      const safe = Number.isFinite(raw) ? raw : min;
      const stepped = min + Math.round((safe - min) / step) * step;
      return Math.min(max, Math.max(min, stepped));
    }

    async saveNote(note) {
      try {
        await this.request(`${ROOT}cart/update.js`, { note });
        this.setStatus('Cart note saved.', false, true);
      } catch (error) {
        this.setStatus(error.message || 'The cart note could not be saved.', true);
      }
    }

    async changeQuantity(lineKey, quantity) {
      this.setBusy(true);
      try {
        const state = await this.request(`${ROOT}cart/change.js`, {
          id: lineKey,
          quantity,
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        });
        this.render(state);
        this.setStatus(quantity === 0 ? 'Item removed from your cart.' : 'Cart updated.', false, true);
      } catch (error) {
        this.setStatus(error.message || 'Your cart could not be updated.', true);
      } finally {
        this.setBusy(false);
      }
    }

    selectedVariant(picker) {
      if (!picker) return null;
      const selectedOptions = [...picker.querySelectorAll('[data-zb-option]')].map((select) => select.value);
      const variants = this.readJson(picker.querySelector('[data-zb-variants]'), []);
      return variants.find(
        (candidate) => candidate.options.length === selectedOptions.length
          && candidate.options.every((option, index) => option === selectedOptions[index]),
      ) || null;
    }

    previewVariant(picker) {
      if (!picker) return;
      const status = picker.querySelector('[data-zb-variant-status]');
      const button = picker.querySelector('[data-zb-variant-update]');
      const variant = this.selectedVariant(picker);

      if (!variant) {
        if (button) button.disabled = true;
        this.setInlineStatus(status, 'This option combination is not available.');
        return;
      }
      if (!variant.available) {
        if (button) button.disabled = true;
        this.setInlineStatus(status, 'This option combination is currently sold out.');
        return;
      }
      if (String(variant.id) === String(picker.dataset.currentVariantId)) {
        if (button) button.disabled = true;
        this.setInlineStatus(status, 'Current selection.', false);
        return;
      }

      if (button) button.disabled = false;
      this.setInlineStatus(status, 'Selection ready to update.', false);
    }

    refreshVariantPickers() {
      this.querySelectorAll('[data-zb-variant-picker]').forEach((picker) => {
        this.previewVariant(picker);
        this.syncVariantTitle(picker);
      });
    }

    syncVariantTitle(picker) {
      const item = picker?.closest('[data-zb-cart-item]');
      const titleLink = item?.querySelector('.zb-cart-item__title a');
      if (!titleLink) return;

      const baseTitle = titleLink.dataset.zbBaseTitle || titleLink.textContent.trim();
      const optionValues = [...picker.querySelectorAll('[data-zb-option]')]
        .map((select) => select.value.trim())
        .filter(Boolean);

      titleLink.dataset.zbBaseTitle = baseTitle;
      titleLink.textContent = optionValues.length ? `${baseTitle} — ${optionValues.join(' / ')}` : baseTitle;
    }

    fixShoppingLinks() {
      const selectors = [
        'a[href$="/collections/all"]',
        'a[href*="/collections/all?"]',
      ];

      this.querySelectorAll(selectors.join(',')).forEach((link) => {
        link.setAttribute('href', SHOP_ALL_URL);
      });

      document.querySelectorAll(`cart-drawer ${selectors.join(',cart-drawer ')}`).forEach((link) => {
        link.setAttribute('href', SHOP_ALL_URL);
      });
    }

    async changeVariant(picker) {
      if (!picker) return;

      const status = picker.querySelector('[data-zb-variant-status]');
      const lineData = this.readJson(picker.querySelector('[data-zb-line-data]'), {});
      const variant = this.selectedVariant(picker);

      if (!variant || !variant.available || String(variant.id) === String(picker.dataset.currentVariantId)) {
        this.previewVariant(picker);
        return;
      }

      this.setInlineStatus(status, 'Updating your selection…', false);
      this.setBusy(true);

      let addedKey = null;
      let previousQuantity = 0;
      let oldLineRemoved = false;

      try {
        const beforeCart = await this.request(`${ROOT}cart.js`, null, 'GET');
        const itemPayload = {
          id: Number(variant.id),
          quantity: Number(picker.dataset.quantity) || 1,
          properties: lineData.properties || {},
        };
        if (lineData.sellingPlan) itemPayload.selling_plan = Number(lineData.sellingPlan);

        const addedState = await this.request(`${ROOT}cart/add.js`, { items: [itemPayload] });
        const addedItem = addedState.items?.[0] || addedState;
        addedKey = addedItem.key;
        if (!addedKey) throw new Error('The selected size or design could not be added.');

        previousQuantity = beforeCart.items?.find((item) => item.key === addedKey)?.quantity || 0;

        await this.request(`${ROOT}cart/change.js`, {
          id: picker.dataset.lineKey,
          quantity: 0,
        });
        oldLineRemoved = true;

        const state = await this.request(`${ROOT}cart/update.js`, {
          updates: {},
          sections: this.sectionIds(),
          sections_url: window.location.pathname,
        });

        this.render(state);
        this.setStatus('Size, design, product name and image updated.', false, true);
      } catch (error) {
        if (addedKey && !oldLineRemoved) {
          try {
            await this.request(`${ROOT}cart/change.js`, { id: addedKey, quantity: previousQuantity });
          } catch (rollbackError) {
            console.error('ZB cart rollback failed:', rollbackError);
          }
        }

        if (oldLineRemoved) {
          this.setStatus('Selection was updated. Refreshing your cart…', false);
          window.location.reload();
          return;
        }

        this.setInlineStatus(status, error.message || 'This selection could not be updated.');
        this.setStatus(error.message || 'This selection could not be updated.', true);
      } finally {
        this.setBusy(false);
      }
    }

    sectionIds() {
      return [this.dataset.sectionId, 'cart-icon-bubble', 'cart-live-region-text'].filter(Boolean);
    }

    render(state) {
      const noteValue = this.querySelector('[data-zb-cart-note]')?.value;
      const sectionHtml = state.sections?.[this.dataset.sectionId];
      if (sectionHtml) {
        const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const source = documentFragment.querySelector('.js-zb-cart-content');
        const target = this.querySelector('.js-zb-cart-content');
        if (source && target) target.innerHTML = source.innerHTML;
      }

      if (typeof noteValue === 'string') {
        const newNote = this.querySelector('[data-zb-cart-note]');
        if (newNote) newNote.value = noteValue;
      }

      this.replaceExternalSection('cart-icon-bubble', state.sections?.['cart-icon-bubble']);
      this.replaceExternalSection('cart-live-region-text', state.sections?.['cart-live-region-text']);
      this.fixShoppingLinks();
      this.refreshVariantPickers();
    }

    replaceExternalSection(id, html) {
      if (!html) return;
      const current = document.getElementById(id);
      if (!current) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.getElementById(id) || parsed.querySelector('.shopify-section');
      if (replacement) current.innerHTML = replacement.innerHTML;
    }

    async request(url, payload, method = 'POST') {
      const options = {
        method,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      };

      if (method === 'GET') {
        options.cache = 'no-store';
      } else {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(payload || {});
      }

      const response = await fetch(url, options);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status || data.errors) {
        const errorMessage = typeof data.errors === 'string'
          ? data.errors
          : data.description || data.message || 'Something went wrong. Please try again.';
        throw new Error(errorMessage);
      }
      return data;
    }

    readJson(node, fallback) {
      try {
        return node ? JSON.parse(node.textContent) : fallback;
      } catch (error) {
        console.error('Invalid ZB cart data:', error);
        return fallback;
      }
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.setAttribute('aria-busy', String(isBusy));
      this.querySelector('.js-zb-cart-content')?.classList.toggle('is-updating', isBusy);
      this.querySelectorAll('button, input, select, textarea').forEach((element) => {
        if (isBusy) {
          element.dataset.zbWasDisabled = String(element.disabled);
          element.disabled = true;
        } else if (element.dataset.zbWasDisabled !== undefined) {
          element.disabled = element.dataset.zbWasDisabled === 'true';
          delete element.dataset.zbWasDisabled;
        }
      });
    }

    setStatus(message, isError = false, temporary = false) {
      const status = this.querySelector('[data-zb-cart-status]');
      if (!status) return;

      status.textContent = message;
      status.classList.toggle('is-error', isError);
      if (temporary && message) {
        window.setTimeout(() => {
          if (status.textContent === message) status.textContent = '';
        }, 2500);
      }
    }

    setInlineStatus(status, message, isError = true) {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', isError && Boolean(message));
    }
  }

  if (!customElements.get('zb-cart-page')) {
    customElements.define('zb-cart-page', ZBCartPage);
  }
})();
